import axios from "axios";

const SUPER_ADMIN = "1702";

const baseURL = process.env.VUE_APP_ARVORE_URL;

const SET_USUARIO = "setUsuario";
const SET_SESSION_CARREGADA = "setSessionCarregada";
const SET_USUARIO_INSTITUCIONAL_ATIVO = "setUsuarioInstitucionalAtivo";
const REQUEST_IDENTIDADE_CHANGE = "requestIdentidadeChange";
const CLEAR_PENDING_IDENTIDADE = "clearPendingIdentidade";
const REGISTER_INTERCEPTOR_IDENTIDADE = "registerInterceptorIdentidade";
const UNREGISTER_INTERCEPTOR_IDENTIDADE = "unregisterInterceptorIdentidade";

const state = {
    usuario: null,
    sessionCarregada: false,
    usuarioInstitucionalAtivo:
        JSON.parse(localStorage.getItem("usuarioInstitucionalAtivo")) || null,
    usuarioInstitucionalPendente: null,
    trocaIdentidadePendente: false,
    interceptadoresIdentidade: new Set(),
};

const isAuthenticated = (state) => state.usuario !== null;

const isAdmin = (state, getters, _rootState, rootGetters) => {
    if (!getters.isAuthenticated) {
        return false;
    }

    const grupos = new Set(
        rootGetters["contexto/contexto"]?.grupos?.map((grupo) => grupo.id),
    );

    return (
        state.usuario?.grupos &&
        state.usuario.grupos.some((el) => grupos.has(el))
    );
};

const isSuperAdmin = (state) => {
    if (!getters.isAuthenticated) {
        return false;
    }

    return state.usuario.grupos?.includes(SUPER_ADMIN);
};

const getters = {
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    sessionCarregada: (state) => state.sessionCarregada,
    username: (state) => state.usuario?.nome,
    cpf: (state) => state.usuario?.cpf,
    email: (state) => state.usuario?.email,
    grupos: (state) => state.usuario?.grupos,
    nome: (state) => state.usuario?.nome,
    matricula: (state) => state.usuario?.matricula,
    usuarioInstitucionalAtivo: (state) => state.usuarioInstitucionalAtivo,
    usuarioInstitucionalPendente: (state) => state.usuarioInstitucionalPendente,
    trocaIdentidadePendente: (state) => state.trocaIdentidadePendente,
    estaAtuandoComoInstitucional: (state) =>
        state.usuarioInstitucionalAtivo !== null,
    identidadeEfetiva: (state) => {
        if (state.usuarioInstitucionalAtivo) {
            return {
                tipo: "institucional",
                id: state.usuarioInstitucionalAtivo.id,
                nome: state.usuarioInstitucionalAtivo.nome,
                email: state.usuarioInstitucionalAtivo.email,
                cpf: null,
                matricula: null,
            };
        }

        return {
            tipo: "pessoa",
            id: state.usuario?.cpf || state.usuario?.email,
            nome: state.usuario?.nome,
            email: state.usuario?.email,
            cpf: state.usuario?.cpf,
            matricula: state.usuario?.matricula,
        };
    },
};

const actions = {
    FETCH_SESSION: async ({ commit }) => {
        try {
            const { data } = await axios.get(baseURL + "/auth/session", {
                withCredentials: true,
            });
            commit(SET_USUARIO, data);
        } catch (e) {
            commit(SET_USUARIO, null);
            commit(SET_USUARIO_INSTITUCIONAL_ATIVO, null);
        } finally {
            commit(SET_SESSION_CARREGADA);
        }
    },
    LOGIN: ({ commit }) => {
        commit(SET_USUARIO_INSTITUCIONAL_ATIVO, null);
        const redirectUri = encodeURIComponent(window.location.origin);
        window.location.href = `${baseURL}/auth/login?redirect_uri=${redirectUri}`;
    },
    LOGOUT: async () => {
        // Não commitamos SET_USUARIO_INSTITUCIONAL_ATIVO aqui: alterar o estado
        // reativo dispararia os watchers de identidadeEfetiva.id (Appbar,
        // listas de chamados), que fariam requisições concorrentes com o
        // fetch de logout. Essas requisições voltariam com 401 e o interceptor
        // do Api.js redirecionaria para o login antes do redirect do CAS.
        localStorage.removeItem("usuarioInstitucionalAtivo");

        const res = await fetch(baseURL + "/auth/logout", {
            method: "POST",
            credentials: "include",
        });
        const { logoutUrl } = await res.json();
        window.location.href = logoutUrl;
    },
    assumirUsuarioInstitucional: ({ state, commit }, usuarioInstitucional) => {
        if (state.interceptadoresIdentidade.size === 0) {
            commit(SET_USUARIO_INSTITUCIONAL_ATIVO, usuarioInstitucional);
            commit(CLEAR_PENDING_IDENTIDADE);
        } else {
            commit(REQUEST_IDENTIDADE_CHANGE, usuarioInstitucional);
        }
    },
    voltarParaMeuUsuario: ({ state, commit }) => {
        if (state.interceptadoresIdentidade.size === 0) {
            commit(SET_USUARIO_INSTITUCIONAL_ATIVO, null);
            commit(CLEAR_PENDING_IDENTIDADE);
        } else {
            commit(REQUEST_IDENTIDADE_CHANGE, null);
        }
    },
    confirmarMudancaIdentidade: ({ state, commit }) => {
        commit(
            SET_USUARIO_INSTITUCIONAL_ATIVO,
            state.usuarioInstitucionalPendente,
        );
        commit(CLEAR_PENDING_IDENTIDADE);
    },
    cancelarMudancaIdentidade: ({ commit }) => {
        commit(CLEAR_PENDING_IDENTIDADE);
    },
    interceptarTrocaIdentidade: ({ commit }, id) => {
        commit(REGISTER_INTERCEPTOR_IDENTIDADE, id);
    },
    cancelarInterceptacaoIdentidade: ({ commit }, id) => {
        commit(UNREGISTER_INTERCEPTOR_IDENTIDADE, id);
    },
};

const mutations = {
    [SET_USUARIO]: (state, usuario) => {
        state.usuario = usuario;
    },
    [SET_SESSION_CARREGADA]: (state) => {
        state.sessionCarregada = true;
    },
    [SET_USUARIO_INSTITUCIONAL_ATIVO]: (state, usuarioInstitucional) => {
        state.usuarioInstitucionalAtivo = usuarioInstitucional;

        if (usuarioInstitucional) {
            localStorage.setItem(
                "usuarioInstitucionalAtivo",
                JSON.stringify(usuarioInstitucional),
            );
        } else {
            localStorage.removeItem("usuarioInstitucionalAtivo");
        }
    },
    [REQUEST_IDENTIDADE_CHANGE]: (state, usuarioInstitucional) => {
        state.usuarioInstitucionalPendente = usuarioInstitucional;
        state.trocaIdentidadePendente = true;
    },
    [CLEAR_PENDING_IDENTIDADE]: (state) => {
        state.usuarioInstitucionalPendente = null;
        state.trocaIdentidadePendente = false;
    },
    [REGISTER_INTERCEPTOR_IDENTIDADE]: (state, id) => {
        state.interceptadoresIdentidade.add(id);
    },
    [UNREGISTER_INTERCEPTOR_IDENTIDADE]: (state, id) => {
        state.interceptadoresIdentidade.delete(id);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
